import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { getDateDetailsFromDateTimeObj, addHoursToDateTime } from 'utils/functions/dateTime';
import { fetchAllOrders } from 'modules/labs/orders/actions';
import {
  orderStatusEnum,
  orderStatusColorEnum,
  orderStatusToShowInFilter,
} from 'modules/labs/orders/enums';
import ordersStrings from 'modules/labs/orders/strings';
import OrdersContainer from 'modules/shared/orders/containers/OrdersContainer';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const OrdersView = props => {
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const { allOrdersApiCallBody, selectedDateRangeFilterObj } = props;
  const [ordersApiCallParams, setOrdersApiCallParams] = useState(allOrdersApiCallBody);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState({
    key: 0,
    name: ordersStrings.ordersHeader.allOrdersFilterItem,
  });
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const dispatch = useDispatch();
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const ordersList = useSelector(state => state.labsOrders.ordersList);
  const totalResultsCount = ordersList?.details?.totalCount;
  const numberOfPages = (totalResultsCount && Math.ceil(totalResultsCount / 10)) || 1;

  const orderStatusFilterItems = orderStatusToShowInFilter.map(filter => ({
    key: filter,
    name: orderStatusEnum.getKeyByValue(filter),
  }));
  const ordersStatusFilterList = [
    {
      key: 0,
      name: ordersStrings.ordersHeader.allOrdersFilterItem,
    },
    ...orderStatusFilterItems,
  ];
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleOrderStatusFilterClick = selectedStatusObj => {
    setSelectedStatusFilter(selectedStatusObj);
    setSelectedPageFilter(1);

    const body = {
      ...ordersApiCallParams,
    };
    if (selectedStatusObj.key !== 0) {
      delete body.OrderBy;
      body.stateId = selectedStatusObj.key;
    } else {
      delete body.stateId;
      body.OrderBy = 'createdOn.desc';
    }

    setOrdersApiCallParams(body);

    dispatch(fetchAllOrders(body));
  };

  const handleOrderSearchBarInputChange = (searchQuery, reset) => {
    setSelectedPageFilter(1);

    const body = {
      ...ordersApiCallParams,
    };
    if (searchQuery) body.searchTerm = searchQuery;
    if (reset) delete body.searchTerm;

    setOrdersApiCallParams(body);

    dispatch(fetchAllOrders(body));
  };

  const handleOrderDateFilterChange = selectedDateObj => {
    const { startDate, endDate } = selectedDateObj;

    setSelectedPageFilter(1);

    const body = {
      ...ordersApiCallParams,
    };
    // adding 2 hours because axios by default treats date as local so
    // it converts it to UTC while sending it
    // +2 hours removed to convert to Egypt time
    body.beginDate = startDate;
    // adding 2 hours because axios by default treats date as local so
    // it converts it to UTC while sending it plus 24 hours to include
    // the selected date in the search results
    body.endDate = addHoursToDateTime(endDate, 24);

    setOrdersApiCallParams(body);

    dispatch(fetchAllOrders(body));
  };

  const handleOrderPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const body = {
      ...ordersApiCallParams,
    };
    body.pageNumber = selectedPageNumber;

    dispatch(fetchAllOrders(body));
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  const { startDate, endDate } = selectedDateRangeFilterObj;
  const {
    day: startDay,
    month: startMonth,
    year: startYear,
  } = getDateDetailsFromDateTimeObj(startDate);
  const { day: endDay, month: endMonth, year: endYear } = getDateDetailsFromDateTimeObj(endDate);
  const selectedDateLabel = `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
  const selectedDateFilterObj = {
    startDate,
    endDate,
    selectedDateLabel,
  };

  return (
    <OrdersContainer
      stringsData={ordersStrings}
      selectedDateFilterObj={selectedDateFilterObj}
      selectedStatusFilter={selectedStatusFilter}
      ordersStatusFilterList={ordersStatusFilterList}
      selectedPageFilter={selectedPageFilter}
      numberOfPages={numberOfPages}
      ordersList={ordersList}
      mappedOrdersList={ordersList.details?.mappedOrdersList || []}
      orderStatusColorEnum={orderStatusColorEnum}
      handleOrderStatusFilterClick={handleOrderStatusFilterClick}
      handleOrderSearchBarInputChange={handleOrderSearchBarInputChange}
      handleOrderDateFilterChange={handleOrderDateFilterChange}
      handleOrderPaginationClick={handleOrderPaginationClick}
      {...props}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

OrdersView.propTypes = {
  allOrdersApiCallBody: PropTypes.shape({}).isRequired,
  selectedDateRangeFilterObj: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    selectedDateLabel: PropTypes.string,
  }).isRequired,
};

export default OrdersView;
